import { Icon } from './icon/index.ts';
import styles from './OptionRow.module.css';

/**
 * The exercise answer-pool row — no § 14 entry of its own yet, extracted from
 * the two places that each drew half of it: the correct-answer radio in
 * `QuestionOptionsFields` (`multiple_choice`) and the inclusion chip
 * `note-identification`'s note pool now shares the same shape with. One row
 * answers both questions an option asks — is this the correct one, and is it
 * in play at all — so both `ConfigFields.tsx`s reuse it rather than
 * recombining two patterns.
 *
 * THE RADIO is `QuestionOptionsFields`'s own construction: a real
 * `<input type="radio">` under a sibling circle, because § 6 keeps the pure
 * circle for the domain of sound and people and a radio is the one control
 * whose SHAPE — not colour — is what tells it apart from a checkbox. Checked
 * takes § 14 List item's "completed" recipe (`success-surface-soft` + a bare
 * check glyph, no disc) rather than a state invented for it: marking an
 * option correct is a stored administrative fact, the same kind `Badge`
 * already paints `success` for an account's own verified status — not a
 * learner's live pick, which is the case § 13 keeps neutral.
 *
 * THE CHIP is a real toggle button (`aria-pressed`), not a second
 * `Checkbox`: the row already spends § 6's one circle exception on the
 * radio, and a box beside it would draw the same grammar twice.
 */

interface OptionRowProps {
  id?: string;
  /** The radio group this row belongs to — shared by every sibling row. */
  name: string;
  label: string;
  correct: boolean;
  /**
   * The correctness radio's own accessible name — not `label`, which the row
   * already shows as text. E.g. `Marcar "Sol" como a resposta correta`.
   */
  radioLabel: string;
  onCorrectSelect: () => void;
  included: boolean;
  onIncludedChange: (included: boolean) => void;
  includedLabel?: string;
  excludedLabel?: string;
  disabled?: boolean;
}

export function OptionRow({
  id,
  name,
  label,
  correct,
  radioLabel,
  onCorrectSelect,
  included,
  onIncludedChange,
  includedLabel = 'Incluída',
  excludedLabel = 'Excluída',
  disabled = false,
}: OptionRowProps) {
  return (
    <div
      className={styles.row}
      data-correct={correct || undefined}
      data-disabled={disabled || undefined}
    >
      <span className={styles.radio}>
        <input
          id={id}
          className={styles.input}
          type="radio"
          name={name}
          checked={correct}
          disabled={disabled}
          aria-label={radioLabel}
          onChange={onCorrectSelect}
        />
        <span className={styles.dot} aria-hidden="true">
          {correct ? <Icon name="check" size={16} /> : null}
        </span>
      </span>

      <span className={styles.label} data-muted={!included || undefined}>
        {label}
      </span>

      <button
        type="button"
        className={styles.chip}
        data-included={included}
        aria-pressed={included}
        aria-label={`${label}: ${included ? includedLabel : excludedLabel}`}
        disabled={disabled}
        onClick={() => onIncludedChange(!included)}
      >
        {included ? includedLabel : excludedLabel}
      </button>
    </div>
  );
}
